(() => {
  class TermsReader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.body = this.querySelector('[data-terms-body]');
      this.index = this.querySelector('[data-terms-index]');
      this.list = this.querySelector('[data-terms-links]');
      if (!this.body || !this.index || !this.list) return;
      this.formatLegacyContent();
      this.headings = [...this.body.querySelectorAll('h2, h3')].filter(heading => heading.textContent.trim());
      if (!this.headings.length) return;
      this.list.replaceChildren();
      this.headings.forEach((heading, index) => {
        if (!heading.id) {
          let id = `terms-section-${index}`;
          while (document.getElementById(id)) id += '-heading';
          heading.id = id;
        }
        heading.classList.add('terms-reader__heading');
        heading.tabIndex = -1;
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = '#' + encodeURIComponent(heading.id);
        link.textContent = heading.textContent.trim();
        item.append(link);
        this.list.append(item);
      });
      this.links = [...this.list.querySelectorAll('a')];
      this.index.hidden = false;
      this.setAttribute('data-index-ready', '');
      const desktop = matchMedia('(min-width: 750px)');
      const resizeIndex = () => { this.index.querySelector('details').open = desktop.matches; };
      resizeIndex();
      desktop.addEventListener('change', resizeIndex, options);
      this.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        const link = event.target.closest('a[href^="#"]');
        if (!link || !this.contains(link)) return;
        const target = this.hashTarget(link.hash);
        if (!target) return;
        if (this.index.contains(link) && !desktop.matches) this.index.querySelector('details').open = false;
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          target.scrollIntoView({ block: 'start', behavior: 'instant' });
          target.focus({ preventScroll: true });
        });
      }, options);
      window.addEventListener('hashchange', () => this.revealHash(), options);
      window.addEventListener('scroll', () => this.scheduleActive(), { ...options, passive: true });
      window.addEventListener('resize', () => this.scheduleActive(), options);
      this.scheduleActive();
      this.revealHash();
      document.fonts?.ready.then(() => { if (this.isConnected) this.revealHash(); });
    }

    formatLegacyContent() {
      // Older Shopify policies use bold headings and BRs instead of document blocks.
      // Move the existing nodes without rewriting or dropping any policy text.
      const children = [...this.body.children];
      if (!children.length || !children.every(node => node.matches('strong, b, br, a, em, span'))) return;
      const boundary = (node, direction) => {
        let sibling = node[direction];
        while (sibling && (sibling.nodeType === Node.COMMENT_NODE || (sibling.nodeType === Node.TEXT_NODE && !sibling.textContent.trim()))) sibling = sibling[direction];
        return !sibling || (sibling.nodeType === Node.ELEMENT_NODE && sibling.matches('br'));
      };
      const headings = new Set(children.filter(node => node.matches('strong, b') && boundary(node, 'previousSibling') && boundary(node, 'nextSibling')));
      if (!headings.size) return;
      const fragment = document.createDocumentFragment();
      let paragraph = [];
      const flush = () => {
        if (!paragraph.length) return;
        if (paragraph.some(node => node.textContent.trim())) {
          const block = document.createElement('p');
          block.append(...paragraph);
          fragment.append(block);
        } else fragment.append(...paragraph);
        paragraph = [];
      };
      for (const node of [...this.body.childNodes]) {
        if (headings.has(node)) {
          flush();
          const heading = document.createElement('h2');
          for (const attribute of node.attributes) heading.setAttribute(attribute.name, attribute.value);
          heading.append(...node.childNodes);
          fragment.append(heading);
        } else if (node.nodeType === Node.ELEMENT_NODE && node.matches('br')) {
          flush();
          node.setAttribute('data-terms-break', '');
          fragment.append(node);
        } else paragraph.push(node);
      }
      flush();
      this.body.replaceChildren(fragment);
    }

    hashTarget(hash) {
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return null; }
      const target = document.getElementById(id);
      return target && this.contains(target) ? target : null;
    }

    revealHash() {
      const target = this.hashTarget(location.hash);
      if (!target) return;
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
        this.scheduleActive();
      });
    }

    scheduleActive() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.isConnected) return;
        const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        const margin = parseFloat(getComputedStyle(this.headings[0]).scrollMarginTop) || 0;
        const offset = padding + margin + 8;
        let current = 0;
        this.headings.forEach((heading, index) => { if (heading.getBoundingClientRect().top <= offset) current = index; });
        this.links.forEach((link, index) => {
          if (index === current) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }
  if (!customElements.get('terms-reader')) customElements.define('terms-reader', TermsReader);
})();
